import React, { useState, useEffect } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { getApiLog, getConsoleLog, ApiLogEntry, ConsoleLogEntry } from '../utilities/networkLogger';

// ─── Types ───────────────────────────────────────────────────────────────────

interface LogData {
  timestamp: string;
  consoleHistory: ConsoleLogEntry[];
  networkActivity: ApiLogEntry[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildLogText(error: Error, logData: LogData): string {
  const divider = '='.repeat(60);
  const subDivider = '-'.repeat(40);
  const { timestamp, consoleHistory, networkActivity } = logData;

  const errorSection = [
    divider, 'ERROR DETAILS', divider,
    `Message   : ${error.message}`,
    `Timestamp : ${timestamp}`,
    `Page URL  : ${window.location.href}`,
    '', 'Stack Trace:', error.stack || 'N/A',
  ].join('\n');

  const consoleSection = [
    '', divider, `CONSOLE HISTORY (${consoleHistory.length} entries)`, divider,
    ...consoleHistory.map((e, i) => {
      const args = e.args.map((a) => {
        try { return typeof a === 'object' ? JSON.stringify(a) : String(a); } catch { return '[unserializable]'; }
      }).join(' ');
      return `${i + 1}. [${e.level.toUpperCase()}] ${e.timestamp}\n   ${args}`;
    }),
  ].join('\n');

  const networkSection = [
    '', divider, `NETWORK ACTIVITY (${networkActivity.length} entries)`, divider,
    ...networkActivity.map((e, i) => {
      const icon = e.status && e.status < 400 ? '✅' : '❌';
      const responseStr = (() => {
        try { return JSON.stringify(e.response, null, 2); } catch { return String(e.response); }
      })();
      return [
        `${i + 1}. ${icon} ${e.method} ${e.url}`,
        `   Status   : ${e.status ?? 'No Response'}`,
        `   Time     : ${e.timestamp}`,
        `   Response :`,
        responseStr.split('\n').map((l) => `     ${l}`).join('\n'),
        subDivider,
      ].join('\n');
    }),
  ].join('\n');

  return [errorSection, consoleSection, networkSection].join('\n');
}

function printCrashReport(error: Error, logData: LogData) {
  const { timestamp, consoleHistory, networkActivity } = logData;

  console.group('%c[ErrorBoundary] Crash Report', 'color: red; font-weight: bold; font-size: 14px;');

  console.group('%c[Error Details]', 'color: red;');
  console.error('[Message]', error.message);
  console.error('[Stack Trace]', error.stack);
  console.error('[Timestamp]', timestamp);
  console.groupEnd();

  console.group(`%c[Console History - Last ${consoleHistory.length} Logs]`, 'color: purple; font-weight: bold;');
  if (consoleHistory.length === 0) {
    console.log('No console logs recorded.');
  } else {
    const levelColors: Record<string, string> = { log: '⬜', info: '🔵', warn: '🟡', error: '🔴' };
    consoleHistory.forEach((entry) => {
      console.log(`${levelColors[entry.level]} [${entry.level.toUpperCase()}] @ ${entry.timestamp}`, ...entry.args);
    });
  }
  console.groupEnd();

  console.group(`%c[Network Activity - Last ${networkActivity.length} Requests]`, 'color: orange; font-weight: bold;');
  if (networkActivity.length === 0) {
    console.log('No network requests recorded.');
  } else {
    networkActivity.forEach((entry) => {
      const icon = entry.status && entry.status < 400 ? '✅' : '❌';
      const status = entry.status !== null ? entry.status : 'No Response';
      console.groupCollapsed(`${icon} ${entry.method} ${entry.url} → ${status} @ ${entry.timestamp}`);
      console.log('[Response]', entry.response);
      console.groupEnd();
    });
  }
  console.groupEnd();

  console.groupEnd();
}

// ─── Fallback UI ─────────────────────────────────────────────────────────────

function ErrorFallback({ error, logData }: { error: Error; logData: LogData }) {
  useEffect(() => {
    const removeOverlays = () => {
      document.querySelectorAll('iframe').forEach(el => el.remove());
      document.querySelectorAll(
        'ion-loading, ion-backdrop, ion-modal, ion-toast, ion-alert, ion-picker, ion-popover'
      ).forEach(el => el.remove());
    };

    // Remove immediately on mount
    removeOverlays();

    // Also dispatch Escape to dismiss CRA overlay
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', keyCode: 27, bubbles: true }));

    // Watch for CRA re-adding the iframe and remove it again
    const observer = new MutationObserver(removeOverlays);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const handleBackToHome = () => {
    window.location.href = window.location.origin;
  };

  const handleShare = async () => {
    try {
      const text = buildLogText(error, logData);
      const fileName = `fyllo-error-${logData.timestamp || Date.now()}.txt`;
      const file = new File([text], fileName, { type: 'text/plain' });

      // Native share sheet with file (works on Android/iOS with HTTPS or localhost)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Fyllo Error Report', text: 'Error log from Fyllo Support App' });
        return;
      }

      // Native share sheet with text only
      if (navigator.share) {
        await navigator.share({ title: 'Fyllo Error Report', text });
        return;
      }

      // Fallback: download the file directly
      const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[handleShare] ❌ failed:', e);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#121212',
      zIndex: 2147483647,
      pointerEvents: 'all',
      fontFamily: 'sans-serif',
    }}>
      <div style={{ backgroundColor: '#eb445a', padding: '16px 20px', color: '#fff', fontSize: '18px', fontWeight: 600 }}>
        Something went wrong
      </div>
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: '24px',
      }}>
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>⚠️</div>
        <p style={{ color: '#eb445a', fontSize: '22px', fontWeight: 600, margin: '0 0 12px' }}>
          Oops! Something went wrong
        </p>
        <p style={{ color: '#ccc', fontSize: '15px', margin: '0 0 32px' }}>
          {error.message || 'An unexpected error occurred'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', maxWidth: '300px' }}>
          <button onClick={handleBackToHome} style={btnStyle('#3880ff')}>
            🏠 Back to Home
          </button>
          <button onClick={handleShare} style={btnStyle('#5260ff')}>
            📤 Share Logs
          </button>
        </div>
      </div>
    </div>
  );
}

function btnStyle(bg: string): React.CSSProperties {
  return {
    padding: '12px 28px', backgroundColor: bg, color: '#fff',
    border: 'none', borderRadius: '8px', fontSize: '16px',
    fontWeight: 600, cursor: 'pointer', pointerEvents: 'all',
  };
}

// ─── ErrorBoundary ────────────────────────────────────────────────────────────

interface Props { children: React.ReactNode; }

const ErrorBoundary: React.FC<Props> = ({ children }) => {
  const [logData, setLogData] = useState<LogData>({ timestamp: '', consoleHistory: [], networkActivity: [] });

  const handleError = (error: Error, info: { componentStack: string }) => {
    // Remove CRA dev overlay iframe and Ionic overlays that block pointer events
    document.querySelectorAll('iframe').forEach(el => el.remove());
    document.querySelectorAll(
      'ion-loading, ion-backdrop, ion-modal, ion-toast, ion-alert, ion-picker, ion-popover'
    ).forEach(el => el.remove());

    const data: LogData = {
      timestamp: new Date().toISOString(),
      consoleHistory: getConsoleLog(),
      networkActivity: getApiLog(),
    };

    setLogData(data);
    printCrashReport(error, data);
    console.error('[Component Stack]', info.componentStack);
  };

  return (
    <ReactErrorBoundary
      fallbackRender={({ error }) => (
        <ErrorFallback error={error} logData={logData} />
      )}
      onError={handleError}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
